/**
 * Criterion Living - Residential Landing Page JavaScript
 * Complete functionality for animations, interactions, and form handling
 * 
 * FORM APPROACH: Native HTML POST to Salesforce (no fetch/AJAX)
 * The form submits directly to Salesforce's Web-to-Case endpoint.
 * JS intercepts onsubmit ONLY to:
 *   1. Validate fields
 *   2. Populate hidden fields (first/last name, project type, description)
 *   3. Fire Meta Pixel Lead event
 *   4. Then allow the native POST to proceed → Salesforce redirects to retURL
 */

// ============================================
// GLOBAL VARIABLES & STATE
// ============================================
let counterAnimated = false;
let scrollDepthTracked = false;

// ============================================
// UTILITY FUNCTIONS
// ============================================

function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(link => {
        link.addEventListener('click', function(e) {
            e.preventDefault();
            const targetId = this.getAttribute('href').substring(1);
            const targetElement = document.getElementById(targetId);
            if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth' });
            }
        });
    });
}

function trackEvent(eventName, parameters = {}) {
    if (typeof gtag !== 'undefined') {
        gtag('event', eventName, parameters);
    }
    if (typeof dataLayer !== 'undefined') {
        dataLayer.push({ event: eventName, ...parameters });
    }
}

// ============================================
// ANIMATION FUNCTIONS
// ============================================

function animateCounter(elementId, start, end, duration, showPlus = false) {
    const element = document.getElementById(elementId);
    if (!element) return;

    let startTimestamp = null;
    const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        const value = Math.floor(progress * (end - start) + start);
        
        let displayText = value.toLocaleString();
        if (showPlus && progress === 1) {
            displayText += '+';
        }
        
        element.textContent = displayText;
        element.classList.add('counting');
        setTimeout(() => element.classList.remove('counting'), 200);

        if (progress < 1) {
            window.requestAnimationFrame(step);
        }
    };
    window.requestAnimationFrame(step);
}

function initProjectCounter() {
    const section = document.getElementById('trusted-projects');
    if (!section) return;

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting && !counterAnimated) {
                animateCounter('project-counter', 0, 5000, 2000, true);
                counterAnimated = true;
                observer.unobserve(section);
            }
        });
    }, { threshold: 0.5 });

    observer.observe(section);
}

// ============================================
// STICKY CTA FUNCTIONALITY
// ============================================

function updateStickyCTA() {
    const stickyCTA = document.getElementById('stickyCTA');
    const heroSection = document.querySelector('.hero-section');
    if (!stickyCTA || !heroSection) return;
    
    const heroHeight = heroSection.offsetHeight;
    if (window.pageYOffset > heroHeight) {
        stickyCTA.classList.add('visible');
    } else {
        stickyCTA.classList.remove('visible');
    }
}

// ============================================
// CARD HOVER EFFECTS
// ============================================

function initCardHoverEffects() {
    document.querySelectorAll('.card-premium').forEach(card => {
        const img = card.querySelector('img');
        if (img) {
            card.addEventListener('mouseenter', () => {
                img.style.transform = 'scale(1.1)';
            });
            card.addEventListener('mouseleave', () => {
                img.style.transform = 'scale(1)';
            });
        }
    });
}

// ============================================
// SALESFORCE WEB-TO-CASE FORM HANDLING
// (Native POST — no fetch/AJAX)
// ============================================

function initSalesforceForm() {
    const form = document.getElementById('criterionWebToCase');
    if (!form) return;
    
    form.addEventListener('submit', handleFormSubmit);
    initPhoneFormatting();
    initErrorClearing();
}

/**
 * Handle form submission — populate hidden fields, validate, then allow native POST.
 * Returns true to allow submission, or calls e.preventDefault() to block it.
 */
function handleFormSubmit(e) {
    
    // Clear previous errors
    clearAllErrors();
    
    // -------------------------------------------------------
    // STEP 1: Validate
    // -------------------------------------------------------
    if (!validateForm()) {
        e.preventDefault();
        return false;
    }
    
    // Check reCAPTCHA
    if (typeof grecaptcha !== 'undefined') {
        const recaptchaResponse = grecaptcha.getResponse();
        if (!recaptchaResponse) {
            e.preventDefault();
            showError('Please complete the reCAPTCHA verification');
            return false;
        }
    }

    // -------------------------------------------------------
    // STEP 2: Populate hidden fields
    // -------------------------------------------------------
    
    // First Name + Last Name + combined Full Name
    var firstName = document.getElementById('first_name').value.trim();
    var lastName = document.getElementById('last_name').value.trim();
    
    document.getElementById('case_first_name').value = firstName;
    document.getElementById('case_last_name').value = lastName;
    document.getElementById('combined_full_name').value = firstName + ' ' + lastName;

    // Combine Project Type checkboxes
    var checkboxes = document.querySelectorAll('.project-type-checkbox:checked');
    var projectTypes = [];
    checkboxes.forEach(function(cb) {
        if (cb.value === 'Other') {
            var otherText = document.getElementById('project_type_other_text').value.trim();
            projectTypes.push(otherText ? 'Other - ' + otherText : 'Other');
        } else {
            projectTypes.push(cb.value);
        }
    });
    document.getElementById('combined_project_type').value = projectTypes.join(';');

    // Build structured Description
    var email = document.getElementById('email').value.trim();
    var phone = document.getElementById('phone').value.trim();
    var persona = document.getElementById('residential_persona').value;
    var installer = document.getElementById('installer_type');
    var installerText = installer ? installer.options[installer.selectedIndex].text : 'N/A';
    var projectStage = document.getElementById('project_stage');
    var projectStageText = projectStage ? projectStage.options[projectStage.selectedIndex].text : 'N/A';
    var projectScope = document.getElementById('project_scope').value.trim();

    document.getElementById('Description').value = 
        'Criterion Living Web Enquiry\n' +
        '-----------------------------\n' +
        'Name: ' + firstName + ' ' + lastName + '\n' +
        'Email: ' + email + '\n' +
        'Phone: ' + phone + '\n' +
        'Persona: ' + persona + '\n' +
        'Installer: ' + installerText + '\n' +
        'Project Type: ' + projectTypes.join(', ') + '\n' +
        'Project Stage: ' + projectStageText + '\n' +
        'Project Scope: ' + projectScope;

    // Update subject with name
    var form = e.target;
    var subjectField = form.querySelector('input[name="subject"]');
    if (subjectField) {
        subjectField.value = 'Criterion Living Web Enquiry - ' + firstName + ' ' + lastName;
    }

    // -------------------------------------------------------
    // STEP 3: Fire tracking events
    // -------------------------------------------------------
    
    // Meta Pixel
    if (typeof fbq === 'function') {
        fbq('track', 'Lead', {
            content_name: 'Criterion Living Enquiry',
            content_category: 'Residential',
            value: 0.00,
            currency: 'AUD'
        });
    }
    
    // GA / Shopify
    trackFormSubmission();

    // -------------------------------------------------------
    // STEP 4: Show loading state, then allow native POST
    // -------------------------------------------------------
    var submitBtn = document.getElementById('submitBtn');
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'SUBMITTING...';
    }

    // Return true → browser submits the form natively to Salesforce
    // Salesforce processes it and redirects to retURL (thank-you page)
    return true;
}

// ============================================
// VALIDATION & ERROR HANDLING
// ============================================

function validateForm() {
    let isValid = true;
    
    const firstName = document.getElementById('first_name');
    if (!firstName || !firstName.value.trim() || firstName.value.trim().length < 2) {
        showFieldError('first_name', 'Please enter your first name');
        isValid = false;
    }
    
    const lastName = document.getElementById('last_name');
    if (!lastName || !lastName.value.trim() || lastName.value.trim().length < 2) {
        showFieldError('last_name', 'Please enter your last name');
        isValid = false;
    }
    
    const email = document.getElementById('email');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email.value)) {
        showFieldError('email', 'Please enter a valid email address');
        isValid = false;
    }
    
    const phone = document.getElementById('phone');
    if (phone) {
        const phoneValue = phone.value.replace(/\D/g, '');
        if (phoneValue.length < 10) {
            showFieldError('phone', 'Please enter a valid phone number (min 10 digits)');
            isValid = false;
        }
    }

    const projectCheckboxes = document.querySelectorAll('.project-type-checkbox:checked');
    if (projectCheckboxes.length === 0) {
        var ptError = document.getElementById('projectTypeError');
        if (ptError) {
            ptError.textContent = 'Please select at least one project type';
            ptError.style.display = 'block';
            ptError.classList.add('show');
        }
        isValid = false;
    }
    
    return isValid;
}

function showFieldError(fieldId, message) {
    const field = document.getElementById(fieldId);
    const errorElement = document.getElementById(fieldId + 'Error');
    
    if (field) {
        field.classList.add('error');
    }
    
    if (errorElement) {
        errorElement.textContent = message;
        errorElement.classList.add('show');
        errorElement.style.display = 'block';
    }
    
    if (document.querySelectorAll('.field-error.show').length === 1 && field) {
        field.focus();
    }
}

function showError(message) {
    const errorMessage = document.getElementById('errorMessage');
    if (!errorMessage) return;
    
    errorMessage.textContent = message;
    errorMessage.classList.add('show');
    errorMessage.style.display = 'block';
    
    errorMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
    
    setTimeout(() => {
        errorMessage.classList.remove('show');
        errorMessage.style.display = 'none';
    }, 7000);
}

function clearAllErrors() {
    const errorMessage = document.getElementById('errorMessage');
    if (errorMessage) {
        errorMessage.classList.remove('show');
        errorMessage.style.display = 'none';
    }
    
    document.querySelectorAll('.form-input, .form-textarea, .sf-form-input, .sf-form-textarea').forEach(field => {
        field.classList.remove('error');
    });
    
    document.querySelectorAll('.field-error').forEach(error => {
        error.classList.remove('show');
        error.style.display = 'none';
    });
}

function trackFormSubmission() {
    trackEvent('form_submit', {
        event_category: 'Contact',
        event_label: 'Criterion Living Web-to-Case',
        form_name: 'Criterion Living Contact',
        form_type: 'Web-to-Case',
        form_location: 'Living Landing Page',
        value: 1
    });
    
    if (typeof Shopify !== 'undefined' && Shopify.analytics) {
        Shopify.analytics.track('Contact Form Submitted', {
            form: 'Criterion Living Web-to-Case',
            source: 'Living Landing Page'
        });
    }
}

function initPhoneFormatting() {
    const phoneField = document.getElementById('phone');
    if (!phoneField) return;
    
    phoneField.addEventListener('input', function(e) {
        let value = e.target.value.replace(/\D/g, '');
        
        if (value.length > 0) {
            if (value.startsWith('04')) {
                if (value.length <= 4) {
                    // Do nothing
                } else if (value.length <= 7) {
                    value = value.slice(0, 4) + ' ' + value.slice(4);
                } else {
                    value = value.slice(0, 4) + ' ' + value.slice(4, 7) + ' ' + value.slice(7, 10);
                }
            }
            else if (value.startsWith('0')) {
                if (value.length <= 2) {
                    // Do nothing
                } else if (value.length <= 6) {
                    value = '(' + value.slice(0, 2) + ') ' + value.slice(2);
                } else {
                    value = '(' + value.slice(0, 2) + ') ' + value.slice(2, 6) + ' ' + value.slice(6, 10);
                }
            }
            else if (value.startsWith('61')) {
                if (value.length <= 3) {
                    value = '+' + value;
                } else if (value.length <= 7) {
                    value = '+61 ' + value.slice(2, 3) + ' ' + value.slice(3);
                } else {
                    value = '+61 ' + value.slice(2, 3) + ' ' + value.slice(3, 7) + ' ' + value.slice(7, 11);
                }
            }
        }
        
        e.target.value = value;
    });
}

function initErrorClearing() {
    const fields = document.querySelectorAll('.form-input, .form-textarea, .sf-form-input, .sf-form-textarea');
    
    fields.forEach(field => {
        field.addEventListener('input', function() {
            this.classList.remove('error');
            const errorElement = document.getElementById(this.id + 'Error');
            if (errorElement) {
                errorElement.classList.remove('show');
                errorElement.style.display = 'none';
            }
        });
    });
}

// ============================================
// ANALYTICS & TRACKING
// ============================================

function initAnalytics() {
    document.querySelectorAll('.btn-premium, .btn-accent, .btn-outline').forEach(button => {
        button.addEventListener('click', function() {
            trackEvent('cta_click', {
                button_text: this.textContent.trim(),
                button_location: this.closest('section')?.id || 'unknown'
            });
        });
    });
    
    window.addEventListener('scroll', function() {
        const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
        if (scrollPercent > 75 && !scrollDepthTracked) {
            trackEvent('scroll_depth', { percent: 75 });
            scrollDepthTracked = true;
        }
    });
}

// ============================================
// FAQ FUNCTIONALITY
// ============================================

function initFAQ() {
    initFAQTabs();
    initFAQAccordion();
}

function initFAQTabs() {
    const tabs = document.querySelectorAll('.faq-tab');
    const categories = document.querySelectorAll('.faq-category');
    
    if (tabs.length === 0 || categories.length === 0) return;
    
    tabs.forEach(tab => {
        tab.addEventListener('click', function(e) {
            e.preventDefault();
            const categoryName = this.dataset.category;
            
            tabs.forEach(t => t.classList.remove('active'));
            this.classList.add('active');
            
            categories.forEach(cat => {
                cat.classList.remove('active');
                cat.querySelectorAll('.faq-item.open').forEach(item => {
                    item.classList.remove('open');
                });
            });
            
            const activeCategory = document.querySelector(`.faq-category[data-category="${categoryName}"]`);
            if (activeCategory) activeCategory.classList.add('active');
            
            trackEvent('faq_category_switch', { category: categoryName });
            
            if (window.innerWidth < 768) {
                const faqSection = document.getElementById('faq');
                if (faqSection) {
                    setTimeout(() => {
                        faqSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                }
            }
        });
    });
}

function initFAQAccordion() {
    const faqItems = document.querySelectorAll('.faq-item');
    if (faqItems.length === 0) return;
    
    faqItems.forEach((item, index) => {
        const question = item.querySelector('.faq-question');
        if (!question) return;
        
        question.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            
            const isOpen = item.classList.contains('open');
            
            const category = item.closest('.faq-category');
            if (category) {
                category.querySelectorAll('.faq-item.open').forEach(openItem => {
                    if (openItem !== item) openItem.classList.remove('open');
                });
            }
            
            if (isOpen) {
                item.classList.remove('open');
            } else {
                item.classList.add('open');
                const questionText = question.querySelector('.faq-question-text');
                trackEvent('faq_open', {
                    question: questionText ? questionText.textContent : 'Unknown',
                    category: category ? category.dataset.category : 'unknown'
                });
            }
        });
    });
}

function handleFAQHashNavigation() {
    const hash = window.location.hash;
    
    if (hash.startsWith('#faq-')) {
        const parts = hash.substring(5).split('-');
        const category = parts[0];
        const questionIndex = parseInt(parts[1]) || 0;
        
        setTimeout(() => {
            const categoryTab = document.querySelector(`.faq-tab[data-category="${category}"]`);
            if (categoryTab) {
                categoryTab.click();
                setTimeout(() => {
                    const faqCategory = document.querySelector(`.faq-category[data-category="${category}"]`);
                    if (faqCategory) {
                        const faqItems = faqCategory.querySelectorAll('.faq-item');
                        if (faqItems[questionIndex]) {
                            faqItems[questionIndex].querySelector('.faq-question').click();
                            setTimeout(() => {
                                faqItems[questionIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 300);
                        }
                    }
                }, 100);
            }
        }, 100);
    }
}

// ============================================
// LEGACY FUNCTIONS
// ============================================

window.closeSuccessMessage = function() {
    const successMessage = document.getElementById('successMessage');
    if (successMessage) successMessage.classList.add('hidden');
};

// ============================================
// INITIALIZATION
// ============================================

function initialize() {
    console.log('Initializing Criterion Living page...');
    
    initSmoothScroll();
    initProjectCounter();
    initCardHoverEffects();
    initAnalytics();
    initSalesforceForm();
    initFAQ();
    handleFAQHashNavigation();
    window.addEventListener('hashchange', handleFAQHashNavigation);
    
    window.addEventListener('scroll', () => {
        updateStickyCTA();
    });
    
    if (window.history.replaceState) {
        window.history.replaceState(null, null, window.location.href);
    }
    
    console.log('Initialization complete!');
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
} else {
    initialize();
}